/**
 * EmptyState - Estado vacío
 * Muestra mensaje cuando no hay datos
 */
import PropTypes from 'prop-types';
import { Box, Typography, Button, Paper } from '@mui/material';
import { Inbox } from '@mui/icons-material';

/**
 * @component EmptyState
 * @description Componente para mostrar cuando no hay datos
 * @param {Object} props
 * @param {React.ElementType} [props.icon] - Icono a mostrar
 * @param {string} props.message - Mensaje principal
 * @param {string} [props.description] - Descripción adicional
 * @param {Object} [props.action] - Acción opcional
 *   Ejemplo: { label: 'Crear nuevo', onClick: fn, icon: AddIcon }
 * @param {boolean} [props.compact=false] - Versión compacta
 */
const EmptyState = ({
  icon: Icon = Inbox,
  message,
  description,
  action,
  compact = false,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: compact ? 4 : 8,
        px: 3,
        textAlign: 'center',
        bgcolor: 'grey.50',
        border: '1px dashed',
        borderColor: 'grey.300',
        borderRadius: 2,
      }}
    >
      {/* Icono */}
      <Box
        sx={{
          width: compact ? 60 : 80,
          height: compact ? 60 : 80,
          borderRadius: '50%',
          bgcolor: 'grey.200',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
        }}
      >
        <Icon
          sx={{
            fontSize: compact ? 32 : 40,
            color: 'grey.500',
          }}
        />
      </Box>

      {/* Mensaje principal */}
      <Typography
        variant={compact ? 'body1' : 'h6'}
        color="text.primary"
        sx={{ fontWeight: 600, mb: description || action ? 1 : 0 }}
      >
        {message}
      </Typography>

      {/* Descripción */}
      {description && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: action ? 2 : 0, maxWidth: 400 }}
        >
          {description}
        </Typography>
      )}

      {/* Botón de acción */}
      {action && (
        <Button
          variant={action.variant || 'contained'}
          color={action.color || 'primary'}
          onClick={action.onClick}
          startIcon={action.icon ? <action.icon /> : undefined}
          size={compact ? 'small' : 'medium'}
        >
          {action.label}
        </Button>
      )}
    </Paper>
  );
};

EmptyState.propTypes = {
  icon: PropTypes.elementType,
  message: PropTypes.string.isRequired,
  description: PropTypes.string,
  action: PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    icon: PropTypes.elementType,
    variant: PropTypes.string,
    color: PropTypes.string,
  }),
  compact: PropTypes.bool,
};

export default EmptyState;
